import { Button } from '@/components/commons';
import { VideoUploadField } from '@/components/molecules/VideoUploadField';
import { AyumiService } from '@/services/ayumi';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import { IUpdateAyumiIntroVideoRequest } from '@/types/request';
import { IAyumiIntroVideo } from '@/types/response';
import { handleErrorCommon, notificationMessage, NOTIFY_STATUS } from '@/utils';
import { AYUMI_INTRO_VIDEO_PROFILE } from '@/utils/videoValidation';
import { Alert, Collapse } from 'antd';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';

type FormValues = {
  url: string;
  posterUrl: string;
};

/**
 * 導入ボトムシートの紹介動画。
 *
 * 課題に紐づかない 1 件だけの設定なので、課題一覧の上に畳んだ形で置く。
 * 以前はアプリの定数に URL を直書きしていて、差し替えのたびにアプリを
 * 出し直す必要があった。
 */
export const IntroVideoSection: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { url: '', posterUrl: '' },
  });
  const { handleSubmit, reset, setError, watch } = methods;

  const { refetch, isFetching, isSuccess, isError } = useQuery(
    [ENDPOINT.AYUMI.GET_INTRO_VIDEO],
    () => AyumiService.getIntroVideo(),
    {
      onSuccess: (res: IResponse<IAyumiIntroVideo>) => {
        reset({
          url: res?.data?.url ?? '',
          posterUrl: res?.data?.posterUrl ?? '',
        });
      },
    },
  );

  const { mutate: save, isLoading } = useMutation(
    (body: IUpdateAyumiIntroVideoRequest) =>
      AyumiService.updateIntroVideo(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: '紹介動画を更新しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
      },
      onError: (error: IResponseError) => handleErrorCommon(error, setError),
    },
  );

  const onSubmit = (values: FormValues) => {
    // 空文字は「未入稿に戻す」の意味でそのまま送る
    save({ url: values.url ?? '', posterUrl: values.posterUrl ?? '' });
  };

  const currentUrl = watch('url');

  return (
    <Collapse
      className='mb-4'
      activeKey={isOpen ? ['intro'] : []}
      onChange={(keys) => setIsOpen(keys.length > 0)}
      items={[
        {
          key: 'intro',
          label: (
            <span className='font-semibold'>
              導入ボトムシートの紹介動画
              <span className='ml-2 font-normal text-sm text-gray-500'>
                {isFetching
                  ? '読み込み中…'
                  : isError
                    ? '読み込みに失敗しました'
                    : currentUrl
                    ? '入稿済み'
                    : '未入稿（動画枠を表示しません）'}
              </span>
            </span>
          ),
          children: (
            <FormProvider {...methods}>
              <div className='text-sm text-gray-500 mb-3'>
                あゆみを初めて開いたときに出るボトムシートで再生されます。
                課題ごとの動画とは別物です。
              </div>
              {/*
                現在の設定を読めていないまま保存すると、空文字で既存の動画を
                消してしまう。読み込みに成功するまでは編集させない。
              */}
              {isSuccess ? (
                <>
                  <VideoUploadField
                    name='url'
                    posterName='posterUrl'
                    label='紹介動画'
                    profile={AYUMI_INTRO_VIDEO_PROFILE}
                    allowUrlInput={false}
                    onUploadingChange={setIsUploading}
                  />
                  <Button
                    type='primary'
                    loading={isLoading}
                    disabled={isUploading || isFetching}
                    onClick={handleSubmit(onSubmit)}
                  >
                    保存
                  </Button>
                </>
              ) : isError ? (
                <Alert
                  type='error'
                  showIcon
                  message='現在の紹介動画を読み込めませんでした。'
                  action={
                    <Button size='small' onClick={() => refetch()}>
                      再読み込み
                    </Button>
                  }
                />
              ) : (
                <div className='text-sm text-gray-500'>読み込み中…</div>
              )}
            </FormProvider>
          ),
        },
      ]}
    />
  );
};
